import React, { useEffect, useState } from 'react';

import type { IProps } from './do-not-call-list-container';
import {
  AddModifyDoNotCallListModalState,
  DoNotCallList as DoNotCallListType,
  DoNotCallListsFilter,
  ModalActions,
} from '../../../../types/do-not-call-list';

import toaster from '../../../../../../shared/toaster';

import DoNotCallListHeader from './components/do-not-call-list-header/do-not-call-list-header';
import DoNotCallListTable from './components/do-not-call-list-table/do-not-call-list-table';
import DoNotCallListEmptyUI from './components/do-not-call-list-empty-ui/do-not-call-list-empty-ui';
import AddModifyDoNotCallList from '../modals/add-modify-do-not-call-list/add-modify-do-not-call-list';
import DeleteDoNotCallList from '../modals/delete-do-not-call-list/delete-do-not-call-list';

import {
  executeOnErrorWithErrorCheck,
  executeOnRequestStatus,
  getIsRequestPending,
} from '../../../../../../shared/utils';
import { DoNotCallListsSortBy } from '../../../../enums/do-not-call-list';
import { SortOrder } from '../../../../../../shared/enums/order';
import { NoDNCListFound } from '../../../../../../shared/svg';
import { NoCallDNCListFound } from '../../../../../../shared/svg/no-call-dnc-list-found';
import hasPermission from '../../../../../../shared/utils/access-control/has-permission';
import { Permissions } from '../../../../../../shared/utils/access-control/enums/permissions';


const DoNotCallList: React.FC<IProps> = ({
  doNotCallLists,
  doNotCallListsPaginationOptions,

  getDoNotCallListsRequestStatus,
  showLoading,
  hideLoading,
  getDoNotCallListsRequestError,
  sendGetDoNotCallListsRequest,
  resetGetDoNotCallListsRequest,

  createDoNotCallListRequestStatus,
  createDoNotCallListRequestError,
  sendCreateDoNotCallListRequest,
  resetCreateDoNotCallListRequest,
  resetCreatedDoNotCallListId,

  updateDoNotCallListRequestStatus,
  updateDoNotCallListRequestError,
  sendUpdateDoNotCallListRequest,
  resetUpdateDoNotCallListRequest,

  deleteDoNotCallListRequestStatus,
  deleteDoNotCallListRequestError,
  sendDeleteDoNotCallListRequest,
  resetDeleteDoNotCallListRequest,

  agencyClients,
  sendGetAgencyClientsDropdownRequest,
  agencyConfigRequestStatus,
}) => {


  const [filters, setFilters] = useState<DoNotCallListsFilter>({
    page: 1,
    limit: 25,
    search: '',
    sortByKey: DoNotCallListsSortBy.UpdatedAt,
    order: SortOrder.DESC,
  });

  const [
    selectedDoNotCallList,
    setSelectedDoNotCallList,
  ] = useState<DoNotCallListType>(null);

  const [
    addModifyModalState,
    setAddModifyModalState,
  ] = useState<AddModifyDoNotCallListModalState>({
    show: false,
    action: 'add',
  });

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const onShowAddModifyModal = (action: ModalActions) => {
    setAddModifyModalState({ show: true, action });
  };

  const onHideAddModifyModal = () => {
    setAddModifyModalState({ show: false, action: 'add' });
    setSelectedDoNotCallList(null);
  };

  const onShowDeleteModal = () => {
    setShowDeleteModal(true);
  };

  const onHideDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedDoNotCallList(null);
  };

  const onFiltersChange = (partialFilters: Partial<DoNotCallListsFilter>) => {
    const updatedFilters = { ...filters, ...partialFilters };
    setFilters(updatedFilters);
    sendGetDoNotCallListsRequest(updatedFilters);
  };

  const onAction = (key: string, doNotCallList: DoNotCallListType) => {
    if (key === 'edit') {
      onShowAddModifyModal('modify');
      setSelectedDoNotCallList(doNotCallList);
      return;
    }
    if (key === 'delete') {
      onShowDeleteModal();
      setSelectedDoNotCallList(doNotCallList);
    }
  };

  const onDeleteDoNotCallList = () => {
    sendDeleteDoNotCallListRequest(selectedDoNotCallList.id);
  };

  useEffect(() => {
    sendGetDoNotCallListsRequest(filters);
  }, []);

  useEffect(() => {
    executeOnRequestStatus({
      status: getDoNotCallListsRequestStatus,
      onPending: () => {
        showLoading();
      },
      onSuccess: () => {
        hideLoading();
        resetGetDoNotCallListsRequest();
      },
      onFailed: () => {
        hideLoading();
        resetGetDoNotCallListsRequest();
        executeOnErrorWithErrorCheck({
          error: getDoNotCallListsRequestError,
          onError: () => {
            toaster.error(getDoNotCallListsRequestError?.message);
          },
        });
      },
    });
  }, [getDoNotCallListsRequestStatus]);

  useEffect(() => {
    executeOnRequestStatus({
      status: createDoNotCallListRequestStatus,
      onSuccess: () => {
        toaster.success('List added successfully');
        onHideAddModifyModal();
        sendGetDoNotCallListsRequest(filters);
        resetCreateDoNotCallListRequest();
        resetCreatedDoNotCallListId();
      },
      onFailed: () => {
        resetCreateDoNotCallListRequest();
        executeOnErrorWithErrorCheck({
          error: createDoNotCallListRequestError,
          onError: () => {
            toaster.error(createDoNotCallListRequestError?.message);
          },
        });
      },
    });
  }, [createDoNotCallListRequestStatus]);

  useEffect(() => {
    executeOnRequestStatus({
      status: updateDoNotCallListRequestStatus,
      onSuccess: () => {
        toaster.success('List modified successfully');
        sendGetDoNotCallListsRequest(filters);
        onHideAddModifyModal();
        resetUpdateDoNotCallListRequest();
      },
      onFailed: () => {
        resetUpdateDoNotCallListRequest();
        onHideAddModifyModal();
        executeOnErrorWithErrorCheck({
          error: updateDoNotCallListRequestError,
          onError: () => {
            toaster.error(updateDoNotCallListRequestError?.message);
          },
        });
      },
    });
  }, [updateDoNotCallListRequestStatus]);

  useEffect(() => {
    executeOnRequestStatus({
      status: deleteDoNotCallListRequestStatus,
      onSuccess: () => {
        toaster.success('List deleted successfully');

        if (doNotCallListsPaginationOptions?.itemCount === 1) {
          onFiltersChange({ page: 1 });
        } else {
          sendGetDoNotCallListsRequest(filters);
        }

        resetDeleteDoNotCallListRequest();
        onHideDeleteModal();
      },
      onFailed: () => {
        resetDeleteDoNotCallListRequest();
        onHideDeleteModal();
        executeOnErrorWithErrorCheck({
          error: deleteDoNotCallListRequestError,
          onError: () => {
            toaster.error(deleteDoNotCallListRequestError?.message);
          },
        });
      },
    });
  }, [deleteDoNotCallListRequestStatus]);

  const isLoading = getIsRequestPending(getDoNotCallListsRequestStatus);

  const renderTableJSX = () => {
    if ((!doNotCallLists || doNotCallLists.length === 0) && !isLoading && filters.search === '') {
      return (
        <DoNotCallListEmptyUI
          icon={<NoCallDNCListFound />}
          title={
            hasPermission(Permissions.DNC_WRITE)
              ? 'Get started by creating a list to manage phone numbers you want to exclude from your outreach.'
              : 'Contact your agency owner to manage phone numbers you want to exclude from your outreach.'
          }
          {...(hasPermission(Permissions.DNC_WRITE) && {
            btnLabel: 'Add List',
            btnHandler: () => onShowAddModifyModal('add'),
          })}
        />
      );
    }

    if ((!doNotCallLists || doNotCallLists.length === 0) && !isLoading) {
      return (
        <DoNotCallListEmptyUI
          icon={<NoDNCListFound />}
          title="No Search Result"
          desc="We can't find any list matching your search."
        />
      );
    }

    return (
      <DoNotCallListTable
        doNotCallLists={doNotCallLists}
        doNotCallListsPaginationOptions={doNotCallListsPaginationOptions}
        filters={filters}
        onFiltersChange={onFiltersChange}
        onAction={onAction}
        isLoading={isLoading}
      />
    );
  };

  return (
    <div className="do-no-contact-list">
      <DoNotCallListHeader
        filters={filters}
        onFiltersChange={onFiltersChange}
        onAddList={onShowAddModifyModal}
        isAgencyConfigRequestPending={getIsRequestPending(agencyConfigRequestStatus)}
      />

      {renderTableJSX()}

      <AddModifyDoNotCallList
        show={addModifyModalState.show}
        action={addModifyModalState.action}
        isLoading={
          getIsRequestPending(createDoNotCallListRequestStatus) ||
          getIsRequestPending(updateDoNotCallListRequestStatus)
        }
        defaultDoNotCallList={selectedDoNotCallList}
        agencyClients={agencyClients}
        onClose={onHideAddModifyModal}
        onAddDoNotCallList={sendCreateDoNotCallListRequest}
        onUpdateDoNotCallList={sendUpdateDoNotCallListRequest}
        sendGetAgencyClientsDropdownRequest={sendGetAgencyClientsDropdownRequest}
      />

      <DeleteDoNotCallList
        show={showDeleteModal}
        title="Delete List"
        contents={[
          'Are you sure you want to delete the selected list?',
          'Once deleted, all the phone numbers in this list will be removed from the do-not-call list.',
        ]}
        isLoading={getIsRequestPending(deleteDoNotCallListRequestStatus)}
        onHide={onHideDeleteModal}
        onSubmit={onDeleteDoNotCallList}
      />
    </div>
  );
};

export default DoNotCallList;
